import { useState, useEffect, useCallback } from 'react';
import api from 'services/api';
import { useTenant } from 'context/TenantContext';

function buildTree(people) {
  const map = {};
  people.forEach(p => { map[p.id] = { ...p, children: [] }; });
  const roots = [];
  people.forEach(p => {
    if (p.superieur_id && map[p.superieur_id]) {
      map[p.superieur_id].children.push(map[p.id]);
    } else {
      roots.push(map[p.id]);
    }
  });
  return roots;
}

function OrgNode({ node, level = 0, onSelect }) {
  const [expanded, setExpanded] = useState(true);
  const hasChildren = node.children.length > 0;
  const colors = ['#8B1A1A', '#1565c0', '#2e7d32', '#7b1fa2', '#f57c00', '#00897b'];
  const bg = colors[level % colors.length];

  return (
    <div style={{ marginLeft: level > 0 ? 32 : 0 }}>
      <div onClick={() => onSelect(node)} style={{
        display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px', marginBottom: 4,
        background: '#fff', border: '1px solid #e0e0e0', borderRadius: 8, cursor: 'pointer',
        borderLeft: `4px solid ${bg}`, transition: 'all 0.15s',
      }}>
        <div style={{ width: 36, height: 36, borderRadius: '50%', background: bg, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
          {(node.prenom?.[0] || '')}{(node.nom?.[0] || '')}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600, fontSize: 13 }}>{node.prenom} {node.nom}</div>
          <div style={{ fontSize: 11, color: '#888' }}>{node.poste || '-'} {node.departement ? `— ${node.departement}` : ''}</div>
        </div>
        {hasChildren && (
          <button onClick={e => { e.stopPropagation(); setExpanded(!expanded); }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: '#888' }}>
            {expanded ? '▾' : '▸'}
          </button>
        )}
      </div>
      {expanded && hasChildren && (
        <div style={{ borderLeft: `2px solid ${bg}30`, marginLeft: 18, paddingLeft: 0 }}>
          {node.children.map(child => <OrgNode key={child.id} node={child} level={level + 1} onSelect={onSelect} />)}
        </div>
      )}
    </div>
  );
}

export default function OrganigramPage() {
  // IDENTITE TENANT — ce document est REMIS AU CLIENT ou au salarie. Il portait
  // « GLADIATOR SECURITY SYSTEMS » EN DUR : l'identite de l'EDITEUR imprimee sur
  // le document d'un tenant. Meme famille que les 4 fuites corrigees en juillet-aout
  // sur les PDF backend, mais cote frontend — non couverte par la matrice du garde-fou.
  const tenant = useTenant();

  const [people, setPeople] = useState([]);
  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [zoom, setZoom] = useState(1);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const r = await api.get('/personnel-admin/organigramme');
      setPeople(r.data);
      setTree(buildTree(r.data));
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const exportPdf = () => {
    const w = window.open('', '_blank', 'width=900,height=700');
    const renderNode = (node, level = 0) => {
      const indent = level * 40;
      const colors = ['#8B1A1A', '#1565c0', '#2e7d32', '#7b1fa2', '#f57c00'];
      const c = colors[level % colors.length];
      let html = `<div style="margin-left:${indent}px;padding:6px 12px;margin-bottom:4px;border-left:3px solid ${c};font-size:12px">
        <strong>${node.prenom} ${node.nom}</strong> — <span style="color:#888">${node.poste || ''}</span>
      </div>`;
      node.children.forEach(child => { html += renderNode(child, level + 1); });
      return html;
    };
    const content = tree.map(n => renderNode(n)).join('');
    w.document.write(`<html><head><title>Organigramme</title>
      <style>body{font-family:Arial,sans-serif;padding:30px}
      .header{border-bottom:2px solid #8B1A1A;padding-bottom:12px;margin-bottom:20px}
      </style></head><body>
      <div class="header"><strong style="font-size:16px;color:#8B1A1A">${tenant?.nom || ''}</strong><br/>Organigramme du personnel</div>
      ${content}</body></html>`);
    w.document.close();
    setTimeout(() => w.print(), 300);
  };

  return (
    <div>
      <div className="content-card" style={{ marginBottom: '1rem' }}>
        <div className="gss-toolbar">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <div className="content-card-title" style={{ margin: 0, border: 'none', padding: 0 }}>Organigramme</div>
            <span style={{ fontSize: 12, color: 'var(--gss-text-light)' }}>({people.length} personnes)</span>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <button className="gss-btn-sm" onClick={() => setZoom(z => Math.max(0.6, z - 0.1))}>-</button>
            <span style={{ fontSize: 12, minWidth: 40, textAlign: 'center' }}>{Math.round(zoom * 100)}%</span>
            <button className="gss-btn-sm" onClick={() => setZoom(z => Math.min(1.5, z + 0.1))}>+</button>
            <button className="gss-btn" onClick={exportPdf}>Exporter PDF</button>
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '1rem' }}>
        <div className="content-card" style={{ flex: 1, padding: '1.5rem', minHeight: 400 }}>
          {loading ? (
            <p style={{ textAlign: 'center', padding: '2rem', color: 'var(--gss-text-light)' }}>Chargement...</p>
          ) : tree.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#999' }}>
              <p>Aucun personnel enregistre</p>
              <p style={{ fontSize: 12 }}>Ajoutez du personnel administratif avec hierarchie pour voir l'organigramme</p>
            </div>
          ) : (
            <div style={{ transform: `scale(${zoom})`, transformOrigin: 'top left', transition: 'transform 0.2s' }}>
              {tree.map(node => <OrgNode key={node.id} node={node} onSelect={setSelected} />)}
            </div>
          )}
        </div>

        {selected && (
          <div className="content-card" style={{ width: 280, padding: '1.5rem', height: 'fit-content' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 12 }}>
              <div style={{ fontSize: 14, fontWeight: 700 }}>{selected.prenom} {selected.nom}</div>
              <button onClick={() => setSelected(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: '#888' }}>&times;</button>
            </div>
            <div style={{ fontSize: 12, lineHeight: 2, color: 'var(--gss-text-secondary)' }}>
              <div><strong>Matricule :</strong> {selected.matricule}</div>
              <div><strong>Poste :</strong> {selected.poste || '-'}</div>
              <div><strong>Departement :</strong> {selected.departement || '-'}</div>
              <div><strong>Subordonnes :</strong> {selected.children?.length || 0}</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
